// Builds a self-contained HTML page for a journey graph: zoom, pan, find, and real text that can be
// selected and copied. No outside files, so it opens anywhere, including offline.

const escapeHtml = value => String(value)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const STYLE = `
* { box-sizing: border-box; }
html, body { height: 100%; margin: 0; }
body { display: flex; flex-direction: column; background: #F5F7FA; color: #1E3A5F; font: 13px Helvetica, Arial, sans-serif; }
header { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 10px 16px; background: #FFFFFF; border-bottom: 1px solid #E2E8F0; }
header h1 { margin: 0; font-size: 16px; }
header .meta { color: #5C6E8A; font-size: 12px; }
.tools { display: flex; align-items: center; gap: 6px; margin-left: auto; }
button, input { height: 32px; border: 1px solid #E2E8F0; border-radius: 8px; background: #FFFFFF; color: #1E3A5F; font: inherit; }
button { min-width: 32px; padding: 0 10px; font-weight: 600; cursor: pointer; }
button:hover { background: #E6F5F4; color: #0B7C72; }
input { width: 220px; padding: 0 10px; outline: none; }
input:focus { border-color: #0D9488; box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.18); }
#count { min-width: 60px; color: #5C6E8A; font-size: 12px; }
main { position: relative; flex: 1; overflow: hidden; cursor: grab; }
main.panning { cursor: grabbing; }
main svg { width: 100%; height: 100%; display: block; }
svg text { cursor: text; user-select: text; -webkit-user-select: text; }
/* Not teal or navy: those are the Start and Next App fills, where selected text would look unselected */
svg text::selection { fill: #FFFFFF; background: #2563EB; }
g.hit > polygon, g.hit > ellipse, g.hit > path:first-of-type { stroke: #F59E0B; stroke-width: 4px; }
g.hit-current > polygon, g.hit-current > ellipse { stroke: #E05263; stroke-width: 5px; }
.find-mark { fill: #FDE047; }
g.hit-current .find-mark { fill: #FB923C; }
.hint { position: absolute; left: 12px; bottom: 12px; padding: 6px 10px; border-radius: 8px; background: rgba(255, 255, 255, 0.92); color: #5C6E8A; font-size: 12px; pointer-events: none; }
`

// Plain ES5 so it runs in any browser the file is opened in
const SCRIPT = `
(function () {
  var main = document.querySelector('main');
  var svg = main.querySelector('svg');
  // Copy it: viewBox.baseVal is live and would follow every zoom, so Fit would go nowhere
  var vb = svg.viewBox.baseVal;
  var full = { x: vb.x, y: vb.y, width: vb.width, height: vb.height };
  var view = { x: full.x, y: full.y, w: full.width, h: full.height };
  function apply () { svg.setAttribute('viewBox', view.x + ' ' + view.y + ' ' + view.w + ' ' + view.h); }
  function fit () { view = { x: full.x, y: full.y, w: full.width, h: full.height }; apply(); }
  // Graph units per screen pixel (the SVG keeps its aspect ratio, so take the larger side)
  function unitsPerPixel () { return Math.max(view.w / main.clientWidth, view.h / main.clientHeight); }
  function zoom (factor, clientX, clientY) {
    var box = main.getBoundingClientRect();
    var upp = unitsPerPixel();
    var cx = view.x + view.w / 2 + ((clientX === undefined ? box.left + box.width / 2 : clientX) - box.left - box.width / 2) * upp;
    var cy = view.y + view.h / 2 + ((clientY === undefined ? box.top + box.height / 2 : clientY) - box.top - box.height / 2) * upp;
    var w = view.w / factor;
    var h = view.h / factor;
    if (w > full.width * 4 || w < full.width / 400) return;
    view.x = cx - (cx - view.x) * (w / view.w);
    view.y = cy - (cy - view.y) * (h / view.h);
    view.w = w;
    view.h = h;
    apply();
  }
  // The node's box in viewBox units. getBBox alone ignores the graph's own transform
  // (Graphviz shifts everything with translate()), so map it through the node's matrix.
  function boxInView (el) {
    var b = el.getBBox();
    var m = svg.getScreenCTM().inverse().multiply(el.getScreenCTM());
    var p1 = svg.createSVGPoint(); p1.x = b.x; p1.y = b.y; p1 = p1.matrixTransform(m);
    var p2 = svg.createSVGPoint(); p2.x = b.x + b.width; p2.y = b.y + b.height; p2 = p2.matrixTransform(m);
    return { x: Math.min(p1.x, p2.x), y: Math.min(p1.y, p2.y), width: Math.abs(p2.x - p1.x), height: Math.abs(p2.y - p1.y) };
  }
  // Moves the stage to the middle of the screen, keeping the current zoom
  function centreOn (el) {
    var b = boxInView(el);
    view.x = b.x + b.width / 2 - view.w / 2;
    view.y = b.y + b.height / 2 - view.h / 2;
    apply();
  }
  // Yellow marks behind each matching piece of text (SVG text has no background of its own)
  function clearMarks () {
    Array.prototype.forEach.call(svg.querySelectorAll('.find-mark'), function (m) { m.parentNode.removeChild(m); });
  }
  function markText (node, words) {
    Array.prototype.forEach.call(node.querySelectorAll('text'), function (t) {
      var text = t.textContent.toLowerCase();
      var box = t.getBBox();
      words.forEach(function (w) {
        var from = text.indexOf(w);
        while (from !== -1) {
          try {
            var x1 = t.getStartPositionOfChar(from).x;
            var x2 = t.getEndPositionOfChar(from + w.length - 1).x;
            var mark = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            mark.setAttribute('class', 'find-mark');
            mark.setAttribute('x', Math.min(x1, x2) - 1);
            mark.setAttribute('y', box.y);
            mark.setAttribute('width', Math.abs(x2 - x1) + 2);
            mark.setAttribute('height', box.height);
            mark.setAttribute('rx', 2);
            t.parentNode.insertBefore(mark, t);
          } catch (e) {}
          from = text.indexOf(w, from + w.length);
        }
      });
    });
  }

  main.addEventListener('wheel', function (e) {
    e.preventDefault();
    zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX, e.clientY);
  }, { passive: false });

  // Drag the background to pan; drag over text to select it
  var drag = null;
  main.addEventListener('mousedown', function (e) {
    if (e.button !== 0 || e.target.closest('text')) return;
    drag = { x: e.clientX, y: e.clientY };
    main.classList.add('panning');
    e.preventDefault();
  });
  window.addEventListener('mousemove', function (e) {
    if (!drag) return;
    var upp = unitsPerPixel();
    view.x -= (e.clientX - drag.x) * upp;
    view.y -= (e.clientY - drag.y) * upp;
    drag = { x: e.clientX, y: e.clientY };
    apply();
  });
  window.addEventListener('mouseup', function () { drag = null; main.classList.remove('panning'); });

  document.getElementById('zoomIn').onclick = function () { zoom(1.4); };
  document.getElementById('zoomOut').onclick = function () { zoom(1 / 1.4); };
  document.getElementById('fit').onclick = fit;

  // Find: highlights every stage whose text matches; Enter steps through them
  var nodes = Array.prototype.slice.call(svg.querySelectorAll('g.node'));
  var hits = [];
  var current = -1;
  var input = document.getElementById('find');
  var count = document.getElementById('count');
  function show () {
    nodes.forEach(function (n) { n.classList.remove('hit-current'); });
    if (current >= 0 && hits[current]) {
      hits[current].classList.add('hit-current');
      centreOn(hits[current]);
    }
    count.textContent = hits.length ? (current + 1) + ' of ' + hits.length : (input.value.trim() ? 'No match' : '');
  }
  input.addEventListener('input', function () {
    var words = input.value.toLowerCase().split(/\\s+/).filter(Boolean);
    nodes.forEach(function (n) { n.classList.remove('hit'); });
    clearMarks();
    hits = !words.length ? [] : nodes.filter(function (n) {
      var text = Array.prototype.map.call(n.querySelectorAll('text'), function (t) { return t.textContent; }).join(' ').toLowerCase();
      return words.every(function (w) { return text.indexOf(w) !== -1; });
    });
    hits.forEach(function (n) { n.classList.add('hit'); markText(n, words); });
    current = hits.length ? 0 : -1;
    show();
  });
  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && hits.length) {
      current = (current + (e.shiftKey ? hits.length - 1 : 1)) % hits.length;
      show();
    } else if (e.key === 'Escape') {
      input.value = '';
      input.dispatchEvent(new Event('input'));
    }
  });
  fit();
})();
`

export function buildGraphHtml ({ title, subtitle, svgText }) {
  const svg = svgText
    .replace(/<\?xml[^>]*>/, '')
    .replace(/<!DOCTYPE[^>]*>/i, '')
    // Size comes from the page; the viewBox does the zooming
    .replace(/<svg([^>]*?)\swidth="[^"]*"/, '<svg$1')
    .replace(/<svg([^>]*?)\sheight="[^"]*"/, '<svg$1')
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>${STYLE}</style>
</head>
<body>
<header>
  <h1>${escapeHtml(title)}</h1>
  <span class="meta">${escapeHtml(subtitle)}</span>
  <div class="tools">
    <input id="find" type="search" placeholder="Find stage, message or PF" aria-label="Find">
    <span id="count"></span>
    <button id="zoomOut" type="button" title="Zoom out">&minus;</button>
    <button id="zoomIn" type="button" title="Zoom in">+</button>
    <button id="fit" type="button" title="Fit to screen">Fit</button>
  </div>
</header>
<main>
${svg}
<div class="hint">Scroll to zoom · Drag to move · Select text to copy · Enter in Find goes to the next match</div>
</main>
<script>${SCRIPT}</script>
</body>
</html>
`
}



// WEBPACK FOOTER //
// ./src/components/shared/graphHtmlExport.js